import { useContext, useEffect, useState } from "react";
import {
  RouteProps as ReactDOMRouteProps,
  Route as ReactDOMRoute,
  Redirect,
} from "react-router-dom";

import SideBar from "../components/SideBar2";
import { AuthContext } from "../contexts/auth";
import { isProjectAdmin } from "../utils/resolveClosingControl";

interface RouteProps extends ReactDOMRouteProps {
  isPrivate?: boolean;
  hasSideBar?: boolean;
  adminOnly?: boolean;
  component: React.ComponentType;
}

const Route: React.FC<RouteProps> = ({
  isPrivate = false,
  hasSideBar = true,
  adminOnly = false,
  component: Component,
  ...rest
}) => {
  const { user, accessOptions, isLoading, userRoleName } =
    useContext(AuthContext);

  const hasDashboardPermission = accessOptions.find(
    (option) => option.name === "Dashboard"
  )?.type?.view?.active;

  const isAdmin = isProjectAdmin(userRoleName, user?.role_id);

  // DEBUG
  if (rest.path === "/routines") {
    console.log("[Route /routines Debug]", {
      isLoading,
      hasUser: !!user,
      userName: user?.name,
      userRoleId: user?.role_id,
    });
  }

  return (
    <ReactDOMRoute
      {...rest}
      render={({ location }) => {
        // DEBUG para /routines
        if (rest.path === "/routines") {
          console.log("[Route /routines render]", {
            isPrivate,
            user: !!user,
            isLoading,
            willShowLoading: isLoading && isPrivate,
            willShowComponent: isPrivate && user,
            willRedirectToLogin: isPrivate && !user && !isLoading,
          });
        }

        // Se ainda está carregando a autenticação, não redireciona ainda
        if (isLoading && isPrivate) {
          return (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100vh"
              }}
            >
              Carregando...
            </div>
          );
        }

        // Se é rota privada e usuário está logado, renderiza o componente
        if (isPrivate && user) {
          // Proteção específica para dashboards: redireciona se não tiver permissão
          if (
            typeof rest.path === "string" &&
            rest.path.startsWith("/dashboard") &&
            !hasDashboardPermission
          ) {
            return (
              <Redirect
                to={{
                  pathname: "/projetos",
                  state: { from: location },
                }}
              />
            );
          }

          // Rotas restritas a administradores (ex.: Blacklist do Dashboard)
          if (adminOnly && !isAdmin) {
            return (
              <Redirect
                to={{
                  pathname: hasDashboardPermission ? "/dashboard" : "/projetos",
                  state: { from: location },
                }}
              />
            );
          }

          return (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                height: "100vh",
                width: "100%",
              }}
            >
              {hasSideBar && <SideBar />}
              <Component />
            </div>
          );
        }

        // Se não é rota privada e usuário não está logado, renderiza o componente
        if (!isPrivate && !user) {
          return (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                height: "100vh",
                width: "100%",
              }}
            >
              <Component />
            </div>
          );
        }

        // Se não é rota privada e usuário está logado, vai para a Tela de Início
        if (!isPrivate && user) {
          return (
            <Redirect
              to={{
                pathname: "/inicio",
                state: { from: location },
              }}
            />
          );
        }

        // Se é rota privada e usuário não está logado, redireciona para login
        console.log("[Route] Redirecting to /login - path:", rest.path);
        return (
          <Redirect
            to={{
              pathname: "/login",
              state: { from: location },
            }}
          />
        );
      }}
    />
  );
};

export { Route };
